import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { finApi, finErr } from "@/lib/financeApi";
import { btnGold } from "@/components/nds/windows/finance/parts";
import { PaycheckRow } from "@/components/nds/windows/finance/PaychecksTab";
import { OwedPaychecks } from "@/components/nds/windows/finance/OwedPaychecks";
import PaySourceList from "@/components/nds/windows/finance/paysource/PaySourceList";

const PS_TABS = ["invoiced", "self_logs", "unassigned"];

export default function PaycheckReviewContent() {
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({});
  const [only, setOnly] = useState("owed");
  const [owed, setOwed] = useState(null);
  const [error, setError] = useState(null);
  const [ps, setPs] = useState(null);
  const loadPs = () => finApi.get("/pay-source").then(setPs).catch((e) => setError(finErr(e)));
  const load = () => {
    loadPs();
    finApi.get("/paychecks-owed").then(setOwed).catch((e) => setError(finErr(e)));
    return finApi.get("/paychecks", { status: "flagged" }).then((d) => setRows(d.paychecks)).catch((e) => setError(finErr(e)));
  };
  const { openWindow } = useFloatingWindows();
  const logFor = (rid) => openWindow("fin_paycheck_form", rid ? { title: `Log paycheck · ${names[rid] || "Resident"}`, residentId: rid } : { title: "Log paycheck" });
  useEffect(() => {
    window.addEventListener("fin:paycheck-logged", load);
    return () => window.removeEventListener("fin:paycheck-logged", load);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    finApi.get("/residents", { status: "all" }).then((d) => setNames(Object.fromEntries((d.residents || d.items || []).map((r) => [r.id, `${r.first_name} ${r.last_name}`])))).catch(() => {});
  }, []);
  const dupes = (rows || []).filter((p) => (p.notes || "").startsWith("Likely duplicate"));
  const shown = (only === "dupes" ? dupes : rows || []).slice().sort((a, b) => (names[a.resident_id] || "").localeCompare(names[b.resident_id] || "") || a.pay_period_end.localeCompare(b.pay_period_end));
  const people = new Set(shown.map((p) => p.resident_id)).size;
  return (
    <div data-testid="paycheck-review-content" className="h-full overflow-y-auto p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {[["owed", `Owed from invoices · ${owed ? owed.count : "…"}`], ["dupes", `Likely duplicates · ${dupes.length}`], ["all", `All flagged · ${(rows || []).length}`],
          ...(ps ? [["invoiced", `Invoiced · ${ps.counts.invoiced}`], ["self_logs", `Self-logs · ${ps.counts.self_logs}`], ["unassigned", `Unassigned · ${ps.counts.unassigned}`]] : [])].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setOnly(id)} data-testid={`paycheck-review-filter-${id}`}
            className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${only === id ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{label}</button>
        ))}
        <button type="button" onClick={() => logFor()} data-testid="paycheck-review-log" className={`${btnGold} inline-flex items-center gap-1.5`}><Plus className="h-3 w-3" /> Log paycheck</button>
        {ps && <span data-testid="ps-missing-count" className={`font-mono text-[10px] uppercase tracking-wider ${ps.counts.missing ? "text-red-300" : "text-nex-muted"}`}>{ps.counts.missing} missing this week</span>}
        {!PS_TABS.includes(only) && only !== "owed" && <span data-testid="paycheck-review-summary" className="ml-auto text-[11px] text-nex-muted">{shown.length} paychecks · {people} residents</span>}
      </div>
      {error && <p data-testid="paycheck-review-error" className="text-sm text-red-300">{error}</p>}
      {PS_TABS.includes(only) ? (ps ? <PaySourceList data={ps} group={only} onChanged={loadPs} /> : <p className="text-sm text-nex-muted">Loading…</p>)
        : only === "owed" ? (owed ? <OwedPaychecks data={owed} reload={load} /> : <p className="text-sm text-nex-muted">Loading…</p>)
        : !rows ? <p className="text-sm text-nex-muted">Loading…</p> : !shown.length ? (
        <p data-testid="paycheck-review-empty" className="text-sm text-nex-muted">Nothing waiting for review.</p>
      ) : shown.map((pc) => (
        <div key={pc.id} data-testid={`paycheck-review-item-${pc.id}`} className="space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nex-gold">{names[pc.resident_id] || pc.resident_id}</div>
          <ul className="list-none"><PaycheckRow pc={pc} onChanged={load} onLog={() => logFor(pc.resident_id)} /></ul>
        </div>
      ))}
    </div>
  );
}
